import { Inject, Injectable, Injector } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { ILobbyConfig } from "./core/config/interfaces";
import { GetConfigUseCase } from "./core/config/get-config.use-case";
import { ISessionService, SESSION_TOKEN } from "@karma-games/angular-devkit/src/lib/modules/authentication";
import { EScreenOrientation } from "@karma-games/angular-devkit/src/lib/std/globals";
import { ErrorPageService } from "./error-page/error-page.service";
import { FullscreenService } from "@karma-games/angular-devkit/src/lib/services/fullscreen";
import { AppOrientationService } from "@karma-games/angular-devkit/src/lib/services/app-orientation";
import {
    isOldBrowser,
    isIframe,
    isIphoneSafari,
    isSafariHideToolbarAvailable
} from "@karma-games/angular-devkit/src/lib/services/browser-detect";
import { SetClientSettingsUseCase } from "@karma-games/angular-devkit/src/lib/modules/settings";
import { AppStorageService } from "@karma-games/angular-devkit/src/lib/services/app-storage";
import { IPopupRef, PopupService } from "@karma-games/angular-devkit/src/lib/modules/popup";
import BannedError = DomainErrors.BannedError;
import { GetUserErrorUseCase, GetUserIsFetchedUseCase, GetUserUseCase, IUser } from "@karma-games/angular-devkit/src/lib/modules/user";
import { DomainErrors, DomainErrorType, ErrorFactory, IError } from "@karma-games/angular-devkit/src/lib/classes/errors";
import { IUseCase, ResponseInterfaces } from "@karma-games/angular-devkit/src/lib/interfaces";


@Injectable({ providedIn: "root" })
export class AppStartUseCase implements IUseCase<void, Promise<boolean>> {

    constructor(
        @Inject(SESSION_TOKEN) private sessionService: ISessionService,
        private getUserUseCase: GetUserUseCase,
        private getConfigUseCase: GetConfigUseCase,
        private fullscreenService: FullscreenService,
        private injector: Injector,
        private appOrientationService: AppOrientationService,
        private errorPageService: ErrorPageService,
        private getUserIsFetchedUseCase: GetUserIsFetchedUseCase,
        private getUserErrorUseCase: GetUserErrorUseCase,
        private setClientSettingsUseCase: SetClientSettingsUseCase,
        private appStorageService: AppStorageService,
        private popupService: PopupService
    ) {
    }

    async execute(): Promise<boolean> {
        try {
            if (!this.getUserIsFetchedUseCase.execute()) {
                const userError: IError | undefined = this.getUserErrorUseCase.execute();
                if (<ResponseInterfaces.ResponseError | undefined>(userError?.wrappedError)?.code) {
                    const transformedError: DomainErrorType | undefined = ErrorFactory.createDomainError(<ResponseInterfaces.ResponseError>(userError?.wrappedError));
                    if (transformedError instanceof BannedError) {
                        this.errorPageService.showErrorPage({
                            errorText: "ERROR_BANNED"
                        });
                        return true;
                    } else {
                        this.errorPageService.showErrorPage();
                        return true;
                    }
                } else {
                    this.errorPageService.showErrorPage();
                    return true;
                }
            }

            const data: [ILobbyConfig | undefined, IUser] = await Promise.all([
                this.getConfigUseCase.execute(),
                firstValueFrom(this.getUserUseCase.execute())
            ]);

            if (!this.sessionService.getSession()?.token || !data[1] || !data[1].id || !data[1].currency || !data[0]) {
                this.errorPageService.showErrorPage();
                return true;
            }

            const orientation: EScreenOrientation = await firstValueFrom(this.appOrientationService.subscribeOnOrientationChange());
            if (isIphoneSafari(this.injector) && orientation === EScreenOrientation.Landscape &&
                (!isIframe() || isSafariHideToolbarAvailable(this.injector))) {
                this.fullscreenService.turnOnFullscreen();
            }

            if (isOldBrowser(this.injector) && !this.appStorageService.getItem("optimalGraphicsPopupAlreadyShown")) {
                const popup: IPopupRef = this.popupService.dialog({
                    title: {
                        key: "GAME_IN_LOW_GRAPHICS_MODE_TITLE"
                    },
                    message: {
                        key: "GAME_IN_LOW_GRAPHICS_MODE"
                    },
                    acceptButton: {
                        text: {
                            key: "ENABLE"
                        },
                        action: async () => {
                            try {
                                await this.setClientSettingsUseCase.execute({
                                    forceCanvas: true,
                                    noAnimation: true
                                });
                                this.appStorageService.setItem("optimalGraphicsPopupAlreadyShown", true);
                            } catch (e) {
                                console.error(e);
                            }
                            popup.hide();
                        }
                    },
                    declineButton: {
                        text: {
                            key: "CANCEL"
                        },
                        action: () => {
                            this.appStorageService.setItem("optimalGraphicsPopupAlreadyShown", true);
                            popup.hide();
                        }
                    }
                });
            }

            return true;
        } catch (e) {
            console.error(e);
            this.errorPageService.showErrorPage();
            return true;
        }
    }
}
